import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * SearchableIdSelect — value-driven dropdown with type-to-filter.
 *
 * Props:
 *   value       — the currently selected ID (string) or ""
 *   onChange    — (nextId) => void
 *   options     — [{ id, label, sub?: string }]
 *   placeholder — text shown when nothing is selected
 *   label       — optional field label (renders above the input)
 *   testId      — data-testid prefix
 *   emptyText   — copy when no match
 */
export default function SearchableIdSelect({
  value, onChange, options = [], placeholder = "Search…", label,
  testId = "sid-select", emptyText = "No matches",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const currentLabel = useMemo(() => {
    const o = options.find((x) => x.id === value);
    return o ? o.label : "";
  }, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 60);
    return options.filter((o) => {
      const hay = `${o.label} ${o.sub || ""}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 60);
  }, [options, query]);

  const commit = (id) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={wrapRef} className="relative">
      {label && (
        <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">{label}</div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-white border border-navy/15 rounded-md text-sm text-ink hover:border-emerald-300 focus:border-gold focus:ring-2 focus:ring-gold/30 outline-none"
        data-testid={`${testId}-trigger`}
      >
        <span className={currentLabel ? "text-ink" : "text-ink-faint"}>
          {currentLabel || placeholder}
        </span>
        <div className="flex items-center gap-1">
          {value && (
            <X
              size={12}
              className="text-ink-muted hover:text-rose-600"
              onClick={(e) => { e.stopPropagation(); commit(""); }}
              data-testid={`${testId}-clear`}
            />
          )}
          <ChevronDown size={14} className="text-ink-muted" />
        </div>
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-navy/15 rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-navy/10">
            <Search size={12} className="text-ink-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to filter…"
              autoFocus
              className="flex-1 text-sm bg-transparent outline-none placeholder:text-ink-faint"
              data-testid={`${testId}-input`}
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="text-sm text-ink-faint px-3 py-3 italic">{emptyText}</div>
            )}
            {filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => commit(o.id)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-navy/5 last:border-0 ${
                  o.id === value ? "bg-emerald-50/60" : ""
                }`}
                data-testid={`${testId}-opt-${o.id}`}
              >
                <div className="text-ink font-heading">{o.label}</div>
                {o.sub && <div className="text-[10px] text-ink-muted mt-0.5">{o.sub}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
